import React, { useEffect, useRef, useState } from 'react';
import { buildLyricTimeline, buildLyricPhrases, lyricDisplayAt } from './lyrics';
import LyricsMagnifier from './LyricsMagnifier';

/**
 * The lyrics in a dark pill at the bottom of the score while playing or
 * paused (Display > Lyrics), in one of three modes: 'word', the word being
 * sung, very large; 'phrase', the whole phrase a little smaller, shown a
 * moment early so it can be read ahead; or 'magnify' (experimental), a
 * panel across the bottom of the score with my part drawn large and gliding
 * (LyricsMagnifier) above the phrase. A word or phrase fades in and out
 * fast; the next one cross-fades over it. One colour rule: what is being
 * sung now is the accent colour, everything else is dimmed.
 *
 * Driven by the player's playhead (the same clock as the note highlight).
 * `clearBottom` lifts the pill above a banner at the bottom of the score.
 * `canSeek` lets the magnifier strip be dragged to move the playhead.
 * The magnify panel stays mounted (hidden) while stopped so its drawings
 * are made before Play.
 */

// Keep in step with the lyrics-out animation in mobile-practice.css
const FADE_OUT_MS = 140;

const Lyric = ({ display, leaving }) => {
  const chars = display.words.reduce((n, w) => n + w.syllables.join('').length, 0) + display.words.length - 1;
  return (
    <div
      className={`lyrics-word ${display.phrase ? 'phrase' : ''} ${leaving ? 'out' : ''}`}
      style={{ '--lyrics-chars': Math.max(display.phrase ? 12 : 5, chars) }}
    >
      {display.words.map((word, w) => (
        <React.Fragment key={w}>
          {w > 0 && ' '}
          <span className="lyrics-w">
            {word.syllables.map((text, i) => (
              <span key={i} className={i === word.now ? 'now' : undefined}>{text}</span>
            ))}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
};

const LyricsOverlay = ({ lyrics, controls, inPractice, clearBottom = false, canSeek = false }) => {
  const { addPlayheadListener } = controls;
  const enabled = Boolean(lyrics.on && lyrics.available && inPractice && addPlayheadListener);
  const [shown, setShown] = useState(null);
  const [leaving, setLeaving] = useState(null);
  const shownRef = useRef(null);
  const timelineRef = useRef({ key: null, events: null, length: 0, spans: [], phrases: [] });
  // The player hands out fresh functions on every render; read them late
  const controlsRef = useRef(controls);
  controlsRef.current = controls;

  const { partId, verse, voice, mode } = lyrics;
  const magnify = mode === 'magnify';
  const shownMode = magnify ? 'phrase' : mode;
  // While the magnifier strip is dragged the words follow the finger, not playback
  const scrubRef = useRef(null);
  useEffect(() => {
    const show = (display) => {
      const current = shownRef.current;
      if ((display?.sig ?? null) === (current?.sig ?? null)) return;
      shownRef.current = display;
      if (current && (!display || display.key !== current.key)) setLeaving(current);
      setShown(display);
    };
    if (!enabled) {
      show(null);
      return undefined;
    }
    const key = `${partId}|${verse}|${voice}`;
    // Rebuild when playback was re-prepared (tempo, repeats, swing) or the part changed
    const timeline = () => {
      const events = controlsRef.current.getNotesWithTiming?.() || null;
      const cache = timelineRef.current;
      if (cache.key !== key || cache.events !== events || cache.length !== (events?.length || 0)) {
        const spans = buildLyricTimeline(events, { partId, verse, voice });
        timelineRef.current = { key, events, length: events?.length || 0, spans, phrases: buildLyricPhrases(spans) };
      }
      return timelineRef.current;
    };
    let scrubbing = false;
    scrubRef.current = (time) => {
      scrubbing = time !== null;
      if (scrubbing) show(lyricDisplayAt(timeline(), time, shownMode));
    };
    const onPlayhead = (time) => {
      if (scrubbing) return;
      if (time === null) {
        show(null);
        return;
      }
      show(lyricDisplayAt(timeline(), time, shownMode));
    };
    const stop = addPlayheadListener(onPlayhead);
    return () => {
      scrubRef.current = null;
      stop();
    };
  }, [enabled, addPlayheadListener, partId, verse, voice, shownMode]);

  // The leaving word or phrase goes once its fade-out has run
  useEffect(() => {
    if (!leaving) return undefined;
    const timer = setTimeout(() => setLeaving(null), FADE_OUT_MS + 30);
    return () => clearTimeout(timer);
  }, [leaving]);

  const words = (
    <>
      {leaving && <Lyric key={`out-${leaving.key}`} display={leaving} leaving />}
      {shown && <Lyric key={shown.key} display={shown} />}
    </>
  );
  if (magnify && lyrics.on && lyrics.available) {
    return (
      <div className={`lyrics-magnify ${inPractice ? '' : 'idle'}`} aria-hidden="true">
        <LyricsMagnifier
          controls={controls}
          partId={lyrics.magnifyPartId}
          partVerses={lyrics.magnifyVerses}
          verse={verse}
          playing={Boolean(controls.isPlaying)}
          onSeek={canSeek ? controls.onSeek : null}
          onScrub={(time) => scrubRef.current?.(time)}
        />
        <div className="lyrics-magnify-words">{words}</div>
      </div>
    );
  }
  if (!enabled && !leaving) return null;
  return (
    <div className={`lyrics-overlay ${clearBottom ? 'clear-bottom' : ''}`} aria-hidden="true">
      <div className="lyrics-band">{words}</div>
    </div>
  );
};

export default LyricsOverlay;
